import * as React from 'react'
import { RefreshCw, TriangleAlert } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { PageHeader } from '@/components/visitone/page-header'
import { useT } from '@/components/visitone/visitone-provider'
import { useCurrentTenantId } from '@/components/tenant/tenant-context'
import { useVenueScope } from '@/components/today/venue-scope-context'
import { VenueSelector } from '@/components/today/venue-selector'
import { useAccess } from '@/hooks/use-access'
import { useDismissTodayModule, useTodayContext } from '@/hooks/use-today-context'
import { TodayRequestError, type ModuleId, type Venue } from '@/lib/bff/today'
import { destinationFromPath } from '@/lib/routing'
import { NeedsAttentionModule } from '@/components/today/modules/needs-attention-module'
import { TonightModule } from '@/components/today/modules/tonight-module'
import { PreOrdersModule } from '@/components/today/modules/pre-orders-module'
import { TodaysNumbersModule } from '@/components/today/modules/todays-numbers-module'
import { SetupProgressModule } from '@/components/today/modules/setup-progress-module'
import { RecentActivityModule } from '@/components/today/modules/recent-activity-module'
import type { TodayModuleProps } from '@/components/today/modules/types'

/*
 * Portfolio is never rendered: it lists other tenants, and no screen may show
 * another tenant's status, licence or activity (VIS-326 AC 6). The BFF may
 * still compose it for a Landlord, so it is dropped before rendering.
 */
type RenderedModuleId = Exclude<ModuleId, 'portfolio'>

const MODULE_COMPONENTS: Record<RenderedModuleId, React.ComponentType<TodayModuleProps>> = {
  'needs-attention': NeedsAttentionModule,
  tonight: TonightModule,
  'pre-orders': PreOrdersModule,
  numbers: TodaysNumbersModule,
  'setup-progress': SetupProgressModule,
  'recent-activity': RecentActivityModule,
}

const dateFormat = new Intl.DateTimeFormat('en-GB', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
})

function greetingKey(hour: number, named: boolean): string {
  const part = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening'
  return named ? `today.greeting.${part}` : `today.greeting.${part}Generic`
}

/*
 * The Today dashboard (VIS-257): a role-aware composition of dismissible-for-the
 * day modules, scoped to a venue, that answers "what needs me right now". The
 * BFF owns access and composition; this orchestrates the page frame, venue
 * scope, per-day dismissals and the polite live region, and routes module
 * actions to the nearest built destination. The page itself is gated on
 * `getAccess` before it mounts (VIS-329). Actions into a destination the
 * caller may not open are not offered, and a dismissal the BFF refuses is
 * reported in its module.
 */
export function TodayDashboard({ onOpenRoute }: { onOpenRoute?: (route: string) => void }) {
  const t = useT()
  const tenantId = useCurrentTenantId()
  const { getVenue, setVenue } = useVenueScope()
  const venue = getVenue(tenantId)

  const { context, error, isLoading, retry, mutate } = useTodayContext(tenantId, venue)
  const dismiss = useDismissTodayModule(tenantId, mutate)
  const { canView, tenantName } = useAccess()
  const [refused, setRefused] = React.useState<ModuleId | null>(null)

  const openRoute = React.useCallback(
    (route: string) => {
      onOpenRoute?.(route)
    },
    [onOpenRoute],
  )
  const canOpenRoute = React.useCallback((route: string) => canView(destinationFromPath(route)), [canView])

  const dismissModule = React.useCallback(
    (id: ModuleId) => {
      setRefused(null)
      dismiss(id).catch((error: unknown) => {
        if (error instanceof TodayRequestError && error.status === 403) setRefused(id)
      })
    },
    [dismiss],
  )
  const refusal = React.useMemo(
    () => ({
      title: t('core.access.actionRefusedTitle'),
      body: t('core.access.actionRefusedBody', { tenant: tenantName }),
    }),
    [t, tenantName],
  )

  // Announce polled context changes politely. SWR keeps the reference stable
  // when the data is unchanged, so this only fires on a genuine update.
  const [announcement, setAnnouncement] = React.useState('')
  const firstLoad = React.useRef(true)
  React.useEffect(() => {
    if (!context) return
    if (firstLoad.current) {
      firstLoad.current = false
      return
    }
    setAnnouncement(t('today.updated'))
  }, [context, t])

  const now = new Date()
  const greetingName = context?.greetingName ?? null
  const greeting = t(greetingKey(now.getHours(), Boolean(greetingName)), {
    name: greetingName ?? '',
  })
  // Choosing a venue reloads the context. The selector stays on the venues
  // already loaded for this tenant, so it keeps keyboard focus meanwhile.
  const [loadedVenues, setLoadedVenues] = React.useState<{ tenantId: string | null; venues: Venue[] } | null>(null)
  React.useEffect(() => {
    if (context) setLoadedVenues({ tenantId, venues: context.venues })
  }, [context, tenantId])
  const venues = context?.venues ?? (loadedVenues?.tenantId === tenantId ? loadedVenues.venues : null)
  const activeVenue = venues?.find((entry) => entry.id === venue)
  const venueName = activeVenue?.name ?? t('today.venueScope.all')

  const header = (
    <PageHeader
      title={isLoading ? t('today.title') : greeting}
      summary={`${dateFormat.format(now)} · ${venueName}`}
      action={
        venues ? (
          <VenueSelector venues={venues} value={venue} onChange={(next) => setVenue(tenantId, next)} />
        ) : (
          <Skeleton className="h-10 w-48" />
        )
      }
    />
  )

  let body: React.ReactNode
  if (isLoading) {
    body = (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-56 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  } else if (error || !context) {
    body = (
      <div
        role="alert"
        className="flex flex-col items-start gap-2 rounded-md border border-danger/40 bg-danger-surface p-5"
      >
        <p className="inline-flex items-center gap-2 text-sm font-medium text-danger">
          <TriangleAlert aria-hidden="true" className="size-4" />
          {t('today.context.errorTitle')}
        </p>
        <p className="text-sm text-fg-muted">{t('today.context.errorBody')}</p>
        <Button variant="secondary" size="sm" onClick={() => retry()}>
          <RefreshCw />
          {t('today.context.retry')}
        </Button>
      </div>
    )
  } else {
    const visible = context.modules.filter(
      (id): id is RenderedModuleId => id !== 'portfolio' && !context.dismissed.includes(id),
    )
    body = (
      <div className="flex flex-col gap-4">
        {visible.map((id) => {
          const Module = MODULE_COMPONENTS[id]
          return (
            <Module
              key={id}
              tenantId={tenantId}
              venue={venue}
              headingId={`today-module-${id}`}
              onOpenRoute={openRoute}
              canOpenRoute={canOpenRoute}
              onDismiss={id === 'numbers' ? undefined : () => dismissModule(id)}
              refusal={refused === id ? refusal : undefined}
            />
          )
        })}
      </div>
    )
  }

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col">
      {header}
      {body}
      <p aria-live="polite" className="sr-only" role="status">
        {announcement}
      </p>
    </div>
  )
}
